import { type PrimitiveType } from 'intl-messageformat';
import { type ReactNode } from 'react';
import type React from 'react';
import { useSnapshot } from 'valtio';

import { getIntlForMessage, getIntlStateForMessage } from './i18n-state.ts';
import type { MessageDescriptor } from './intl-types.ts';

type ComponentReplacement = (str: ReactNode) => React.JSX.Element;
type MessageFormatter = Pick<ReturnType<typeof getIntlForMessage>, 'formatMessage'>;

type RequiredDescriptorFields = Pick<
  MessageDescriptor,
  'id' | 'defaultMessage' | 'externalMessagesKey'
>;
interface GetMessageParamsPrimitive {
  message?: RequiredDescriptorFields;
  values?: Record<string, PrimitiveType>;
}

interface GetMessageParams {
  message?: RequiredDescriptorFields;
  values?: Record<string, PrimitiveType | ComponentReplacement>;
}

/**
 * A hook that that returns the translated message string of a message descriptor if it is ready,
 * or the fallback message (the default one, defined in the message descriptor) if it is not.
 *
 * If the message is undefined, it returns the empty string ('').
 */
export function useMessageString({ message, values }: GetMessageParamsPrimitive): string {
  const { intl } = useSnapshot(getIntlStateForMessage(message));

  return formatMessageString({ message, values }, intl);
}

/**
 * A hook that that returns a formatted message as a ReactNode, useful for when you need to pass a
 * component as a <wrapper> to a message, eg for bolding text etc. If the message is undefined,
 * it returns the empty string ('').
 */
export function useMessage({ message, values }: GetMessageParams): ReactNode {
  const { intl } = useSnapshot(getIntlStateForMessage(message));

  return formatMessageWithIntl({ message, values }, intl);
}

/**
 * Shared string formatter for callers that already have an intl object, or need
 * the same external-bundle routing as the hooks outside React render flow.
 */
export function formatMessageString(
  { message, values }: GetMessageParamsPrimitive,
  intl: MessageFormatter = getIntlForMessage(message),
): string {
  return message !== undefined ? intl.formatMessage(message, values) : '';
}

/**
 * Mirrors formatMessageString for rich-text messages so the hook can stay
 * focused on subscribing to the correct intl state before formatting.
 */
function formatMessageWithIntl(
  { message, values }: GetMessageParams,
  intl: MessageFormatter = getIntlForMessage(message),
): ReactNode {
  return message !== undefined ? intl.formatMessage(message, values) : '';
}
